import { lazy, StrictMode, Suspense, useEffect, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router";
import { ProveedorSesion, useSesion } from "./sesion";
import { Cargando, LimiteDeError, ProveedorAvisos, useAvisos } from "./ui";
import { escucharPushEnPrimerPlano, registrarServiceWorker } from "./notificaciones";
import { registrarCuenta, registrarPantalla } from "./analitica";
import Entrar from "./paginas/Entrar";
import Registro from "./paginas/Registro";
import Inicio from "./paginas/Inicio";
import Unirme from "./paginas/Unirme";
import Perfil from "./paginas/Perfil";
import "./estilos.css";

// Cada panel se carga aparte: el residente no descarga el lector de QR ni el panel de administración.
const Residente = lazy(() => import("./paginas/Residente"));
const Domiciliario = lazy(() => import("./paginas/Domiciliario"));
const Vigilante = lazy(() => import("./paginas/Vigilante"));
const Admin = lazy(() => import("./paginas/admin/Admin"));
const Plataforma = lazy(() => import("./paginas/plataforma/Plataforma"));

// Tras publicar una versión nueva, los archivos de la anterior ya no existen: recargamos una vez para traer
// la nueva. Si vuelve a fallar enseguida, no recargamos en bucle: lo muestra LimiteDeError.
window.addEventListener("vite:preloadError", (e) => {
  try {
    const ultima = Number(sessionStorage.getItem("porteria-recarga") ?? 0);
    if (Date.now() - ultima < 30_000) return;
    sessionStorage.setItem("porteria-recarga", String(Date.now()));
  } catch {
    return;
  }
  e.preventDefault();
  location.reload();
});

/** Exige sesión iniciada y registro completo (nombre + documento). Conserva la ruta completa (con ?codigo=…). */
function Protegida({ children, soloSuper }: { children: ReactNode; soloSuper?: boolean }) {
  const { cuenta, perfil, esSuper } = useSesion();
  const ubicacion = useLocation();
  if (cuenta === undefined || (cuenta && perfil === undefined)) return <Cargando />;
  const volver = encodeURIComponent(ubicacion.pathname + ubicacion.search);
  if (!cuenta) return <Navigate to={`/entrar?volver=${volver}`} replace />;
  if (!perfil) return <Navigate to={`/registro?volver=${volver}`} replace />;
  if (soloSuper && esSuper === undefined) return <Cargando />;
  if (soloSuper && !esSuper) return <Navigate to="/inicio" replace />;
  return <>{children}</>;
}

function AvisosEnPrimerPlano() {
  const { avisar } = useAvisos();
  const { cuenta } = useSesion();
  useEffect(() => {
    if (!cuenta) return;
    let quitar: (() => void) | undefined;
    escucharPushEnPrimerPlano((titulo, cuerpo) => avisar(`${titulo}: ${cuerpo}`, "info")).then((f) => (quitar = f));
    return () => quitar?.();
  }, [cuenta, avisar]);
  return null;
}

/** Cada cambio de pantalla llega a Google Analytics, y los eventos quedan ligados a la cuenta. */
function Analitica() {
  const { pathname } = useLocation();
  const { cuenta } = useSesion();
  const uid = cuenta === undefined ? undefined : (cuenta?.uid ?? null);
  useEffect(() => registrarPantalla(pathname), [pathname]);
  useEffect(() => {
    if (uid !== undefined) registrarCuenta(uid);
  }, [uid]);
  return null;
}

function App() {
  const ubicacion = useLocation();
  return (
    <LimiteDeError clave={ubicacion.pathname}>
      <Suspense fallback={<Cargando />}>
        <AvisosEnPrimerPlano />
        <Analitica />
        <Routes>
          <Route path="/" element={<Navigate to="/inicio" replace />} />
          <Route path="/entrar" element={<Entrar />} />
          <Route path="/registro" element={<Registro />} />
          <Route path="/inicio" element={<Protegida><Inicio /></Protegida>} />
          <Route path="/unirme" element={<Protegida><Unirme /></Protegida>} />
          <Route path="/perfil" element={<Protegida><Perfil /></Protegida>} />
          <Route path="/c/:cid/residente/*" element={<Protegida><Residente /></Protegida>} />
          <Route path="/c/:cid/domiciliario/*" element={<Protegida><Domiciliario /></Protegida>} />
          <Route path="/c/:cid/vigilante/*" element={<Protegida><Vigilante /></Protegida>} />
          <Route path="/admin/:cid/*" element={<Protegida><Admin /></Protegida>} />
          <Route path="/plataforma/*" element={<Protegida soloSuper><Plataforma /></Protegida>} />
          <Route path="*" element={<Navigate to="/inicio" replace />} />
        </Routes>
      </Suspense>
    </LimiteDeError>
  );
}

registrarServiceWorker();

createRoot(document.getElementById("raiz")!).render(
  <StrictMode>
    <BrowserRouter>
      <ProveedorAvisos>
        <ProveedorSesion>
          <App />
        </ProveedorSesion>
      </ProveedorAvisos>
    </BrowserRouter>
  </StrictMode>,
);
